import { useMemo, useState, type FormEvent } from 'react';
import {
  V2Card,
  V2CardHeader,
  V2CardBody,
  V2Table,
  V2Badge,
  V2Button,
  V2EmptyState,
  V2FormActions,
  V2FormField,
  V2FormGrid,
  type V2TableColumn,
  type V2BadgeTone,
  Alert,
  LoadingState,
  Icon,
  Switch,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import {
  IRAN_MOBILE_PATTERN,
  IRAN_MOBILE_VALIDATION_MESSAGE,
  isValidIranMobileNumber,
  toEnglishDigits,
} from '../../lib/phoneNumber.js';
import type {
  CustomRoleCreatedResponse,
  InvitationCreatedResponse,
  InviteStatus,
  MembershipListResponse,
  PermissionCatalogGroup,
  PermissionCatalogResponse,
} from '../../lib/types/membership.js';

const STATUS_TONE: Record<string, V2BadgeTone> = {
  active: 'success',
  disabled: 'danger',
  pending: 'warn',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'فعال',
  disabled: 'غیرفعال',
  pending: 'در انتظار پذیرش',
};

const INVITE_STATUS_TONE: Record<InviteStatus, V2BadgeTone> = {
  pending: 'warn',
  sms_sent: 'info',
  sms_failed: 'danger',
  accepted: 'success',
  expired: 'neutral',
  cancelled: 'neutral',
};

const INVITE_STATUS_LABEL: Record<InviteStatus, string> = {
  pending: 'دعوت هنوز ارسال نشده',
  sms_sent: 'دعوت پیامک شد',
  sms_failed: 'ارسال دعوت ناموفق بود',
  accepted: 'دعوت پذیرفته شد',
  expired: 'دعوت منقضی شد',
  cancelled: 'دعوت لغو شد',
};

interface AccessPreset {
  readonly title: string;
  readonly groupIds: readonly string[];
}

/** Ready-made role cards (repo owner's 2026-09-27 review: "کارت نقش‌های
 * آماده... با خلاصهٔ دسترسی") — each maps to real `permissions/catalog`
 * groups, never a fabricated permission set. The catalog's own group
 * granularity is per-module, not per-seniority, so "کارشناس فروش" and
 * "مدیر فروش" share the same group today; a finer split needs the manual
 * per-key toggle below (kept reachable, not removed). */
const ACCESS_PRESETS: readonly AccessPreset[] = [
  { title: 'مدیر فروش', groupIds: ['sales'] },
  { title: 'کارشناس فروش', groupIds: ['sales'] },
  { title: 'حسابدار', groupIds: ['finance'] },
  { title: 'انباردار', groupIds: ['inventory'] },
  { title: 'مسئول تدارکات', groupIds: ['procurement', 'inventory'] },
  { title: 'مدیر پروژه', groupIds: ['projects'] },
];

type Screen = 'list' | 'add-member' | 'add-access';

interface MemberDraft {
  readonly firstName: string;
  readonly lastName: string;
  readonly mobile: string;
  readonly jobTitle: string;
}

const EMPTY_DRAFT: MemberDraft = { firstName: '', lastName: '', mobile: '', jobTitle: '' };

interface TeamTableRow {
  readonly id: string;
  readonly memberRef: string;
  readonly displayName: string;
  readonly loginIdentifier: string;
  readonly roleDisplayNames: readonly string[];
  readonly membershipStatus: string;
  readonly inviteStatus?: InviteStatus;
}

/**
 * UI_REFERENCE_SOURCE: Sakhtar Onboarding Team Desktop.dc.html — C1
 * (اعضای تیم), C2 (افزودن عضو), C3 (دسترسی‌های عضو). Read directly via
 * DesignSync this session (see the Phase 1 shell's own doc comment for the
 * same source). Fidelity: PARTIAL_REAL — C3's per-module cards use one
 * toggle per module (all-or-nothing for that module's real permission
 * keys), not the board's deeper per-capability checkbox tree; every
 * key involved is still a real `permissions/catalog` key, never a
 * fabricated bucket id.
 *
 * Phase 2 of the /setup reconciliation (Phase 1: PR moving this wizard's
 * shell out of AuthenticatedShell). Closes the backend gap Phase 1 flagged:
 * `POST roles` (a caller-chosen permission set, not one of two fixed
 * templates) now backs C3's "تنظیم دستی" screen for real.
 *
 * Deferred invitations (design's own rule — "دعوت‌نامه هنگام تایپ ارسال
 * نمی‌شود؛ فقط پس از تأیید تنظیمات"): C3's confirm creates the real
 * account/membership/custom-role assignment immediately (`deferNotification:
 * true` on `POST invitations`), so the C1 list and access badges are never
 * fabricated — only the SMS itself is withheld. It goes out when this step
 * is confirmed (`تکمیل و ادامه`) or skipped, via the existing, already-real
 * `invitation/resend` endpoint for every membership `GET memberships`
 * reports as `inviteStatus: 'pending'` — never a new send path, and never a
 * client-tracked "who still needs notifying" list: that state is the real
 * server-side invite lifecycle already recorded for ERP-245.
 *
 * "این شخص ادمین است" (repo owner's rule, 2026-09-25): enabled only once
 * every catalog key is selected, because holding the whole catalog IS what
 * `MembershipAdminService#isFullTenantAdmin` checks — there is no separate
 * flag to set. Checking it is a confirming label, not an extra grant.
 */
export function TeamStep({
  base,
  onComplete,
  onBack,
  busy,
  error,
}: {
  base: string;
  onComplete: () => Promise<void>;
  onBack: () => void;
  busy: boolean;
  error: string | null;
}) {
  const [screen, setScreen] = useState<Screen>('list');
  const [draft, setDraft] = useState<MemberDraft>(EMPTY_DRAFT);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<string>>(new Set());
  const [appliedPreset, setAppliedPreset] = useState<string | null>(null);
  const [manualExpanded, setManualExpanded] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [flushError, setFlushError] = useState<string | null>(null);
  const [flushing, setFlushing] = useState(false);

  const members = useApiGet<MembershipListResponse>(`${base}/api/v1/memberships`, [refreshKey]);
  const catalog = useApiGet<PermissionCatalogResponse>(`${base}/api/v1/permissions/catalog`, []);

  const groups: readonly PermissionCatalogGroup[] =
    catalog.status === 'ready' ? catalog.data.groups : [];
  const allKeys = useMemo(() => groups.flatMap((g) => g.keys), [groups]);
  const isFullAdminSelected = allKeys.length > 0 && allKeys.every((k) => selectedKeys.has(k));

  function toggleGroup(group: PermissionCatalogGroup) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const groupIsFullyOn = group.keys.every((k) => next.has(k));
      for (const key of group.keys) {
        if (groupIsFullyOn) next.delete(key);
        else next.add(key);
      }
      return next;
    });
  }

  function applyPreset(preset: AccessPreset) {
    const keys = groups.filter((g) => preset.groupIds.includes(g.id)).flatMap((g) => g.keys);
    setSelectedKeys(new Set(keys));
    setAppliedPreset(preset.title);
    setManualExpanded(false);
  }

  function presetSummary(preset: AccessPreset): string {
    const labels = groups.filter((g) => preset.groupIds.includes(g.id)).map((g) => g.label);
    return labels.length > 0 ? labels.join('، ') : '—';
  }

  function toggleFullAdmin() {
    if (isFullAdminSelected) {
      setSelectedKeys(new Set());
    } else if (allKeys.length > 0) {
      setSelectedKeys(new Set(allKeys));
    }
  }

  function startAddMember() {
    setDraft(EMPTY_DRAFT);
    setDraftError(null);
    setScreen('add-member');
  }

  function backToList() {
    setScreen('list');
  }

  function onDraftSubmit(event: FormEvent) {
    event.preventDefault();
    const firstName = draft.firstName.trim();
    const lastName = draft.lastName.trim();
    const mobile = draft.mobile.trim();
    const jobTitle = draft.jobTitle.trim();

    if (!firstName || !lastName) {
      setDraftError('لطفاً نام و نام خانوادگی همکار را وارد کنید.');
      return;
    }
    if (!isValidIranMobileNumber(mobile)) {
      setDraftError(IRAN_MOBILE_VALIDATION_MESSAGE);
      return;
    }
    if (!jobTitle) {
      setDraftError('لطفاً سمت سازمانی همکار را وارد کنید.');
      return;
    }
    setDraft({ firstName, lastName, mobile, jobTitle });
    setDraftError(null);
    setSelectedKeys(new Set());
    setAppliedPreset(null);
    setManualExpanded(false);
    setAccessError(null);
    setScreen('add-access');
  }

  async function onAccessSubmit(event: FormEvent) {
    event.preventDefault();
    if (selectedKeys.size === 0) {
      setAccessError('لطفاً حداقل یک دسترسی برای این همکار انتخاب کنید.');
      return;
    }
    setSubmitting(true);
    setAccessError(null);
    try {
      const role = await apiPost<CustomRoleCreatedResponse>(`${base}/api/v1/roles`, {
        displayName: draft.jobTitle,
        permissionKeys: [...selectedKeys],
      });
      await apiPost<InvitationCreatedResponse>(`${base}/api/v1/invitations`, {
        mobile: draft.mobile,
        firstName: draft.firstName,
        lastName: draft.lastName,
        roleId: role.roleId,
        deferNotification: true,
      });
      setDraft(EMPTY_DRAFT);
      setSelectedKeys(new Set());
      setScreen('list');
      setRefreshKey((k) => k + 1);
    } catch {
      setAccessError('افزودن عضو ناموفق بود — ممکن است این شماره همراه قبلاً ثبت شده باشد.');
    } finally {
      setSubmitting(false);
    }
  }

  /** Sends the real, already-deferred SMS for every member this step (or an
   * earlier visit to it) added — the design's "فقط پس از تأیید تنظیمات"
   * moment. Reuses `invitation/resend` verbatim; adds no new send path. */
  async function flushPendingInvites(): Promise<boolean> {
    if (members.status !== 'ready') return true;
    const pending = members.data.memberships.filter((m) => m.inviteStatus === 'pending');
    if (pending.length === 0) return true;
    let allOk = true;
    for (const member of pending) {
      try {
        await apiPost(`${base}/api/v1/memberships/${member.memberRef}/invitation/resend`);
      } catch {
        allOk = false;
      }
    }
    return allOk;
  }

  async function handleComplete() {
    setFlushing(true);
    setFlushError(null);
    const ok = await flushPendingInvites();
    setFlushing(false);
    if (!ok) {
      setFlushError(
        'ارسال دعوت‌نامه برای برخی اعضا ناموفق بود. می‌توانید از همین فهرست دوباره ارسال کنید.',
      );
      return;
    }
    await onComplete();
  }

  const rawMemberships = members.status === 'ready' ? members.data.memberships : [];
  const tableRows: TeamTableRow[] = rawMemberships.map((m) => ({
    id: m.memberRef,
    memberRef: m.memberRef,
    displayName:
      m.displayName || (m.loginIdentifier ? `همکار (${m.loginIdentifier})` : 'کاربر دعوت‌شده'),
    loginIdentifier: m.loginIdentifier ?? '—',
    roleDisplayNames: m.roleDisplayNames,
    membershipStatus: m.membershipStatus,
    ...(m.inviteStatus === undefined ? {} : { inviteStatus: m.inviteStatus }),
  }));

  const columns: readonly V2TableColumn<TeamTableRow>[] = [
    {
      key: 'name',
      header: 'نام همکار',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: 'var(--sk-brand-soft, #e0e7ff)',
              color: 'var(--sk-brand-600, #4338ca)',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 700,
              fontSize: 14,
              flexShrink: 0,
            }}
          >
            {row.displayName.charAt(0)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontWeight: 600, fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg)' }}>
              {row.displayName}
            </span>
            <span style={{ fontSize: 'var(--sk-fs-xs)', color: 'var(--sk-fg-muted)' }}>
              {row.roleDisplayNames[0] ?? 'عضو سازمان'}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'mobile',
      header: 'شماره همراه',
      render: (row) => (
        <span
          className="ltr"
          style={{
            fontFamily: 'var(--sk-font-mono)',
            fontSize: 'var(--sk-fs-sm)',
            direction: 'ltr',
            display: 'inline-block',
          }}
        >
          {row.loginIdentifier}
        </span>
      ),
    },
    {
      key: 'access',
      header: 'دسترسی‌ها',
      render: (row) => (
        <V2Badge tone={row.roleDisplayNames.length > 0 ? 'success' : 'neutral'}>
          {row.roleDisplayNames.length > 0 ? 'دسترسی‌ها: تنظیم‌شده' : 'دسترسی‌ها: تنظیم‌نشده'}
        </V2Badge>
      ),
    },
    {
      key: 'status',
      header: 'وضعیت عضویت',
      render: (row) => (
        <V2Badge tone={STATUS_TONE[row.membershipStatus] ?? 'neutral'}>
          {STATUS_LABEL[row.membershipStatus] ?? row.membershipStatus}
        </V2Badge>
      ),
    },
    {
      key: 'invite',
      header: 'وضعیت دعوت',
      render: (row) =>
        row.inviteStatus ? (
          <V2Badge tone={INVITE_STATUS_TONE[row.inviteStatus]}>
            {INVITE_STATUS_LABEL[row.inviteStatus]}
          </V2Badge>
        ) : (
          <span style={{ color: 'var(--sk-fg-muted)' }}>—</span>
        ),
    },
  ];

  if (screen === 'add-member') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <V2Card>
          <V2CardHeader title="افزودن عضو" subtitle="مشخصات همکار خود را وارد کنید." />
          <V2CardBody>
            <form
              onSubmit={onDraftSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
            >
              <V2FormGrid>
                <V2FormField label="نام" required width="medium">
                  <input
                    className="sk-input"
                    placeholder="مثال: علی"
                    value={draft.firstName}
                    onChange={(e) => setDraft((d) => ({ ...d, firstName: e.target.value }))}
                  />
                </V2FormField>
                <V2FormField label="نام خانوادگی" required width="medium">
                  <input
                    className="sk-input"
                    placeholder="مثال: محمدی"
                    value={draft.lastName}
                    onChange={(e) => setDraft((d) => ({ ...d, lastName: e.target.value }))}
                  />
                </V2FormField>
                <V2FormField label="شماره همراه" required width="medium">
                  <input
                    type="tel"
                    inputMode="numeric"
                    dir="ltr"
                    className="sk-input ltr"
                    pattern={IRAN_MOBILE_PATTERN}
                    maxLength={11}
                    placeholder="09xxxxxxxxx"
                    value={draft.mobile}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, mobile: toEnglishDigits(e.target.value) }))
                    }
                  />
                </V2FormField>
                <V2FormField label="سمت سازمانی" required width="wide">
                  <input
                    className="sk-input"
                    list="team-job-title-options"
                    placeholder="مثال: حسابدار، مدیر فروش، انباردار..."
                    value={draft.jobTitle}
                    onChange={(e) => setDraft((d) => ({ ...d, jobTitle: e.target.value }))}
                  />
                  <datalist id="team-job-title-options">
                    {ACCESS_PRESETS.map((preset) => (
                      <option key={preset.title} value={preset.title} />
                    ))}
                  </datalist>
                </V2FormField>
              </V2FormGrid>
              <div style={{ fontSize: 'var(--sk-fs-xs)', color: 'var(--sk-fg-muted)' }}>
                سمت سازمانی توصیفی است و سطح دسترسی همکار را تعیین نمی‌کند — دسترسی‌ها در مرحله بعد
                مشخص می‌شود.
              </div>

              {draftError ? (
                <Alert tone="danger" title="خطا">
                  {draftError}
                </Alert>
              ) : null}

              <V2FormActions>
                <V2Button variant="ghost" onClick={backToList} type="button">
                  انصراف
                </V2Button>
                <span style={{ marginInlineEnd: 'auto' }} />
                <V2Button type="submit" variant="primary" icon="chevronLeft">
                  بعدی: تعیین دسترسی‌ها
                </V2Button>
              </V2FormActions>
            </form>
          </V2CardBody>
        </V2Card>
      </div>
    );
  }

  if (screen === 'add-access') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <V2Card>
          <V2CardHeader
            title={`دسترسی‌های ${draft.firstName} ${draft.lastName}`}
            subtitle="استفاده از پیشنهاد آماده یا تنظیم دستی دسترسی‌ها."
          />
          <V2CardBody>
            <form
              onSubmit={onAccessSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span
                  style={{ fontSize: 'var(--sk-fs-sm)', fontWeight: 600, color: 'var(--sk-fg)' }}
                >
                  استفاده از پیشنهاد — یکی از نقش‌های آماده را انتخاب کنید
                </span>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: 8,
                  }}
                >
                  {ACCESS_PRESETS.map((preset) => {
                    const active = appliedPreset === preset.title;
                    return (
                      <button
                        key={preset.title}
                        type="button"
                        onClick={() => applyPreset(preset)}
                        style={{
                          textAlign: 'start',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 3,
                          padding: '10px 12px',
                          borderRadius: 'var(--sk-r-md)',
                          border: `1.5px solid ${active ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
                          background: active
                            ? 'color-mix(in oklch, var(--sk-brand-500) 6%, var(--sk-bg-elev))'
                            : 'var(--sk-bg-elev)',
                        }}
                      >
                        <span
                          style={{
                            fontSize: 'var(--sk-fs-sm)',
                            fontWeight: 600,
                            color: 'var(--sk-fg)',
                          }}
                        >
                          {preset.title}
                        </span>
                        <span style={{ fontSize: 'var(--sk-fs-xs)', color: 'var(--sk-fg-muted)' }}>
                          {presetSummary(preset)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {!manualExpanded ? (
                <V2Button
                  variant="ghost"
                  size="sm"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => setManualExpanded(true)}
                >
                  ویرایش جزئی دسترسی‌ها (پیشرفته)
                </V2Button>
              ) : null}

              {catalog.status === 'loading' ? <LoadingState /> : null}
              {catalog.status === 'ready' && manualExpanded ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <span
                    style={{ fontSize: 'var(--sk-fs-sm)', fontWeight: 600, color: 'var(--sk-fg)' }}
                  >
                    تنظیم دستی — بخش‌های سامانه
                  </span>
                  {groups.map((group) => {
                    const groupOn =
                      group.keys.length > 0 && group.keys.every((k) => selectedKeys.has(k));
                    return (
                      <div
                        key={group.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          borderRadius: 'var(--sk-r-md)',
                          border: `1.5px solid ${groupOn ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
                          background: groupOn
                            ? 'color-mix(in oklch, var(--sk-brand-500) 6%, var(--sk-bg-elev))'
                            : 'var(--sk-bg-elev)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Icon name="shield" size="sm" />
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: 'var(--sk-fs-sm)',
                              color: 'var(--sk-fg)',
                            }}
                          >
                            {group.label}
                          </span>
                        </div>
                        <Switch label="" checked={groupOn} onChange={() => toggleGroup(group)} />
                      </div>
                    );
                  })}

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      marginTop: 8,
                      borderRadius: 'var(--sk-r-md)',
                      border: '1.5px dashed var(--sk-border)',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 'var(--sk-fs-sm)',
                          color: 'var(--sk-fg)',
                        }}
                      >
                        این شخص ادمین است
                      </span>
                      <span style={{ fontSize: 'var(--sk-fs-xs)', color: 'var(--sk-fg-muted)' }}>
                        فقط وقتی قابل انتخاب است که همه‌ی بخش‌های بالا برای این عضو فعال باشد.
                      </span>
                    </div>
                    <Switch
                      label=""
                      checked={isFullAdminSelected}
                      disabled={!isFullAdminSelected && allKeys.length > selectedKeys.size}
                      onChange={toggleFullAdmin}
                    />
                  </div>
                </div>
              ) : null}

              {accessError ? (
                <Alert tone="danger" title="خطا">
                  {accessError}
                </Alert>
              ) : null}

              <V2FormActions>
                <V2Button variant="ghost" type="button" onClick={() => setScreen('add-member')}>
                  بازگشت
                </V2Button>
                <span style={{ marginInlineEnd: 'auto' }} />
                <V2Button type="submit" variant="primary" icon="plus" disabled={submitting}>
                  {submitting ? 'در حال افزودن…' : 'تایید و افزودن عضو'}
                </V2Button>
              </V2FormActions>
            </form>
          </V2CardBody>
        </V2Card>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <V2CardHeader
          title="اعضای تیم"
          subtitle="همکاران خود را اضافه کنید و دسترسی هر یک را مشخص نمایید."
        />
        <V2CardBody>
          {members.status === 'loading' ? <LoadingState /> : null}
          {members.status === 'ready' ? (
            <V2Table
              columns={columns}
              rows={tableRows}
              zebra
              emptyState={<V2EmptyState icon="user" compact title="هنوز عضوی اضافه نشده" />}
            />
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 16 }}>
            <V2Button variant="primary" icon="plus" onClick={startAddMember}>
              افزودن عضو
            </V2Button>
          </div>
        </V2CardBody>
      </V2Card>

      <Alert tone="info">
        دعوت‌نامه هر عضو هنگام افزودن ارسال نمی‌شود؛ فقط پس از «تکمیل و ادامه» برای همه‌ی اعضای در
        انتظار ارسال می‌شود.
      </Alert>

      {flushError ? (
        <Alert tone="danger" title="خطا">
          {flushError}
        </Alert>
      ) : null}

      {error ? (
        <Alert tone="danger" title="خطا">
          {error}
        </Alert>
      ) : null}

      <V2FormActions>
        <V2Button variant="ghost" disabled={busy || flushing} onClick={onBack}>
          بازگشت
        </V2Button>
        <span style={{ marginInlineEnd: 'auto' }} />
        <V2Button
          variant="primary"
          icon="check"
          disabled={busy || flushing}
          onClick={() => void handleComplete()}
        >
          {busy || flushing ? 'در حال ثبت…' : 'تکمیل و ادامه'}
        </V2Button>
      </V2FormActions>
    </div>
  );
}
